// R18 Task B (Peter #1): the day's manual checks were rendered on the
// employee PDF but not in the expanded detail on the blotter screen —
// "when I open the details on the screen the checks are not listed on the
// screen but they are on the PDF ... I don't need to reinvent things just to
// have that reflected on that screen".
//
// Deliberately a flat table rather than the tiered 20% / 10% / Misc layout
// used by UserReportManualChecksSection: that grouping earns its keep over a
// month, but a single day is usually one or two checks, and this mirrors the
// daily PDF's own checks section (_manual_checks_section with showTier=false)
// so screen and print agree.
import React from 'react';
import type { UserReportManualCheck } from '../../types/report';
import { formatCurrency } from '../../utils/currencyUtils';
import { formatDate } from '../../utils/dateUtils';

interface BlotterEmployeeChecksProps {
  checks?: UserReportManualCheck[];
}

const BlotterEmployeeChecks: React.FC<BlotterEmployeeChecksProps> = ({ checks }) => {
  if (!checks || checks.length === 0) {
    return null;
  }

  const totalAmount = checks.reduce((sum, c) => sum + Number(c.amount || 0), 0);
  const totalCommission = checks.reduce((sum, c) => sum + Number(c.your_commission || 0), 0);

  return (
    <div className="mt-4">
      <h4 className="text-sm font-semibold text-gray-700 mb-2">Manual Checks</h4>
      <div className="overflow-x-auto border border-gray-200 rounded-lg">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Firm</th>
              <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
              <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Payout %</th>
              <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Your Commission</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {checks.map((c) => (
              <tr key={c.id}>
                <td className="px-3 py-2 text-sm text-gray-900">{formatDate(c.check_date)}</td>
                <td className="px-3 py-2 text-sm text-gray-900">{c.firm_name}</td>
                <td className="px-3 py-2 text-sm text-right text-gray-900">{formatCurrency(c.amount)}</td>
                <td className="px-3 py-2 text-sm text-right text-gray-600">
                  {Number(c.payout_percentage).toFixed(2)}%
                </td>
                <td className="px-3 py-2 text-sm text-right font-semibold text-green-700">
                  {formatCurrency(c.your_commission)}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot className="bg-gray-50">
            <tr>
              <td colSpan={2} className="px-3 py-2 text-sm text-right font-medium text-gray-700">
                Checks Total:
              </td>
              <td className="px-3 py-2 text-sm text-right font-bold text-blue-700">
                {formatCurrency(totalAmount)}
              </td>
              <td className="px-3 py-2"></td>
              <td className="px-3 py-2 text-sm text-right font-bold text-green-700">
                {formatCurrency(totalCommission)}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
};

export default BlotterEmployeeChecks;
